import React, { useCallback, useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import Text from '../../../components/text/Text';
import { useTheme } from '../../../theme';
import { UserSelector } from '../../../components/selector';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { getUserName } from '../../../../common/utils/user/getUserName';

const SetupAssigneePicker = ({
	assignees = [],
	onChange,
	disabled,
	placeholder = 'Add assignee',
	returnObjects = false,
	initialFilter,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const [pickerKey, setPickerKey] = useState(0);

	const uniqueAssignees = useMemo(() => {
		const seen = new Set();
		return assignees.filter(assignee => {
			if (!assignee?.id || seen.has(assignee.id)) {
				return false;
			}
			seen.add(assignee.id);
			return true;
		});
	}, [assignees]);

	const emitChange = useCallback((items) => {
		if (returnObjects) {
			onChange(items);
		}
		else {
			onChange(items.map(user => user.id));
		}
	}, [onChange, returnObjects]);

	const handleAdd = useCallback((newUser) => {
		if (!newUser?.id) {
			return;
		}
		if (uniqueAssignees.some(assignee => assignee.id === newUser.id)) {
			setPickerKey(prev => prev + 1);
			return;
		}

		emitChange([...uniqueAssignees, newUser]);
		setPickerKey(prev => prev + 1);
	}, [emitChange, uniqueAssignees]);

	const handleRemove = useCallback((removeId) => {
		emitChange(uniqueAssignees.filter(user => user.id !== removeId));
	}, [emitChange, uniqueAssignees]);

	return (
		<View style={styles.container}>
			{
				uniqueAssignees.length > 0 && (
					<View style={styles.chipContainer}>
						{
							uniqueAssignees.map(assignee => (
								<View key={assignee.id} style={styles.chip}>
									<Text style={styles.chipText}>{getUserName(assignee)}</Text>
									<Pressable
										onPress={() => handleRemove(assignee.id)}
										accessibilityRole="button"
										accessibilityLabel={`Remove ${getUserName(assignee)}`}
										style={({ pressed }) => [styles.removeButton, pressed && styles.pressed]}
									>
										<Feather name="x" size={16} color={colors.onChip} />
									</Pressable>
								</View>
							))
						}
					</View>
				)
			}
			<UserSelector
				key={pickerKey}
				onChange={handleAdd}
				disabled={disabled}
				placeholder={placeholder}
				initialFilter={initialFilter}
				buttonStyle={styles.addButton}
			/>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space, radius, touch } }) => ({
	container: {
		marginTop: space.sm,
	},
	chipContainer: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.sm,
		marginBottom: space.md,
	},
	chip: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.xs,
		backgroundColor: colors.chip,
		paddingLeft: space.md,
		borderRadius: radius.pill,
	},
	chipText: {
		...type.secondaryStrong,
		color: colors.onChip,
		flexShrink: 1,
	},
	removeButton: {
		width: touch.min,
		height: touch.min,
		alignItems: 'center',
		justifyContent: 'center',
	},
	pressed: {
		opacity: 0.7,
	},
	addButton: {
		alignSelf: 'stretch',
	},
});

export default SetupAssigneePicker;
